import {LegacyWBIcon} from '@wandb/common/components/elements/LegacyWBIcon';
import {toast} from '@wandb/common/components/elements/Toast';
import {PopupDropdown} from '@wandb/common/components/PopupDropdown';
import {
  DragHandle,
  DragSource,
  DropTarget,
  useDragDropContext,
} from '@wandb/common/containers/DragDropContainer';
import classNames from 'classnames';
import * as _ from 'lodash';
import React, {
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {Label, Popup} from 'semantic-ui-react';

import {PanelBankUpdaterContext} from '../../state/panelbank/context';
import {usePanelSearchDebouncedContext} from '../../state/panelbank/PanelSearchContextProvider';
import * as CustomRunColorsViewTypes from '../../state/views/customRunColors/types';
import * as ViewHooks from '../../state/views/hooks';
import * as PanelTypes from '../../state/views/panel/types';
import * as PanelBankConfigTypes from '../../state/views/panelBankConfig/types';
import * as PanelBankSectionConfigActions from '../../state/views/panelBankSectionConfig/actions';
import {useAllPanelBankSectionConfigsAction} from '../../state/views/panelBankSectionConfig/hooks';
import * as PanelBankSectionConfigTypes from '../../state/views/panelBankSectionConfig/types';
import * as PanelSettingsTypes from '../../state/views/panelSettings/types';
import * as RunSetViewTypes from '../../state/views/runSet/types';
import {RunHistoryKeyInfo} from '../../types/run';
import {
  isDraggingWithinSection,
  isPanel,
  isPanelBankSection,
  PANEL_BANK_HIDDEN_SECTION_NAME,
} from '../../util/panelbank';
import {EditableLabel} from '../elements/EditableLabel';
import {PanelContainer} from '../Panel';
import * as PanelRunsLinePlot from '../PanelRunsLinePlot';
import {
  LinePlotPanel,
  LocalPanelSettingsComponentControlled,
} from '../PanelSettings';
import {PanelBankFlowSection} from './PanelBankFlowSection';
import {PanelBankGridSection} from './PanelBankGridSection';
import {SectionMetadata} from './PanelBankSections';

type PanelBankSectionProps = SectionMetadata & {
  readOnly?: boolean;
  panelBankConfigRef: PanelBankConfigTypes.Ref;
  panelBankWidth: number;
  // query: Query.Query;
  workspacePanelSettingsRef: PanelSettingsTypes.Ref;
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
  customRunColorsRef: CustomRunColorsViewTypes.Ref;
  runSetRefs: RunSetViewTypes.Ref[];
  historyKeyInfo: RunHistoryKeyInfo;
  addVisButton?: ReactElement;
  defaultMoveToSectionRef?: PanelBankSectionConfigTypes.Ref;
  moveSectionBefore(
    moveRef: PanelBankSectionConfigTypes.Ref,
    beforeSectionRef: PanelBankSectionConfigTypes.Ref
  ): void;
  addSectionAbove(): void;
  addSectionBelow(): void;
  deleteSection(): void;
  movePanel(
    panelRef: PanelTypes.Ref,
    fromSectionRef: PanelBankSectionConfigTypes.Ref,
    toSectionRef: PanelBankSectionConfigTypes.Ref,
    toIndex: number
  ): void;
};

export function usePanelBankSectionActions(
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref
) {
  const toggleType = ViewHooks.useViewAction(
    panelBankSectionConfigRef,
    PanelBankSectionConfigActions.toggleType
  );
  const updateName = ViewHooks.useViewAction(
    panelBankSectionConfigRef,
    PanelBankSectionConfigActions.updateName
  );

  return {
    toggleType,
    updateName,
  };
}

// TODO: move to a util file
function usePanelBankSectionVisibility(
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref
) {
  // isOpen and toggleIsOpen refer to the persisted section visibility state (from the server/redux)
  const {isOpen} = usePanelBankSectionConfig(
    panelBankSectionConfigRef
  ).panelBankSectionConfig;
  const toggleIsOpen = ViewHooks.useViewAction(
    panelBankSectionConfigRef,
    PanelBankSectionConfigActions.toggleIsOpen
  );

  // isOpenWhileSearching provides a temporary override for the section visibility state.
  // When searching panels, we want sections to be open by default but still be toggleable,
  // independent of the persisted section visibility state (panelBankSectionConfig.isOpen)
  // NOTE: this is a temporary state, not saved to the workspace config.
  // We may want to revisit this if users complain
  const [isOpenWhileSearching, setIsOpenWhileSearching] = useState(true);
  const toggleIsOpenTemp = () => setIsOpenWhileSearching(io => !io);

  // Another override!
  // If we're dragging a section, or dragging a panel while holding the shift key, collapse all sections temporarily.
  const {dragRef, shiftKey} = useDragDropContext();
  const isDraggingSection = dragRef != null && isPanelBankSection(dragRef);
  const forceCollapsed = isDraggingSection || shiftKey;

  const {isSearching} = usePanelSearchDebouncedContext();

  // This is the final section visibility state (getter and setter) used in the UI
  const sectionIsOpen =
    !forceCollapsed && (isSearching ? isOpenWhileSearching : isOpen);
  const toggleSectionIsOpen = isSearching ? toggleIsOpenTemp : toggleIsOpen;

  // One more thing...
  // We don't want to render collapsed sections until they're first opened,
  // but we also don't want to re-mount sections (and re-fetch panels) if you collapse and re-open them.
  // So we use this state variable, which flips to 'true' the first time the section is opened.
  const [shouldRenderSection, setShouldRenderSection] = useState(false);
  useEffect(() => {
    if (shouldRenderSection) {
      return;
    }
    setShouldRenderSection(isOpen || isSearching);
  }, [isOpen, isSearching, shouldRenderSection]);

  return useMemo(
    () => ({
      sectionIsOpen,
      toggleSectionIsOpen,
      shouldRenderSection,
    }),
    [sectionIsOpen, shouldRenderSection, toggleSectionIsOpen]
  );
}

function usePanelBankSectionConfig(
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref
) {
  return {
    panelBankSectionConfig: ViewHooks.useWhole(panelBankSectionConfigRef),
  };
}

function usePanelBankSection(
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref
) {
  const [isEditingName, setIsEditingName] = useState(false);
  const [isSettingsPopupOpen, setIsSettingsPopupOpen] = useState(false);

  return {
    isEditingName,
    setIsEditingName,
    isSettingsPopupOpen,
    setIsSettingsPopupOpen,
    ...usePanelSearchDebouncedContext(),
    ...usePanelBankSectionActions(panelBankSectionConfigRef),
    ...usePanelBankSectionVisibility(panelBankSectionConfigRef),
    ...usePanelBankSectionConfig(panelBankSectionConfigRef),
  };
}

export const PanelBankSection = (props: PanelBankSectionProps) => {
  const {
    panelBankConfigRef,
    panelBankSectionConfigRef,
    activePanelRefs,
    inactivePanelRefs,
    panelSearchResults,
    panelSearchMatchCount = 0,
    isSectionNameSearchMatch,
    addSectionAbove,
    addSectionBelow,
    deleteSection,
    customRunColorsRef,
    workspacePanelSettingsRef,
    panelBankWidth,
    moveSectionBefore,
    runSetRefs,
    historyKeyInfo,
    movePanel,
    addVisButton,
    readOnly,
    defaultMoveToSectionRef,
  } = props;

  const {
    panelBankSectionConfig,
    toggleType,
    updateName,
    sectionIsOpen,
    toggleSectionIsOpen,
    shouldRenderSection,
    isSearching,
    makeHighlightSearchTitle,
    isEditingName,
    setIsEditingName,
    isSettingsPopupOpen,
    setIsSettingsPopupOpen,
  } = usePanelBankSection(panelBankSectionConfigRef);

  const normalizedPanelBankConfig = ViewHooks.usePart(
    panelBankSectionConfigRef
  );
  const localPanelSettingsRef = normalizedPanelBankConfig.localPanelSettingsRef;

  const localPanelSettings = ViewHooks.useWhole(localPanelSettingsRef);

  const workspacePanelSettings = ViewHooks.useWhole(workspacePanelSettingsRef);

  const useAllLinePlotPanels = () => {
    return React.useMemo(
      () =>
        panelBankSectionConfig.panels.filter(
          p => p.viewType === PanelRunsLinePlot.PANEL_TYPE
        ) as unknown as LinePlotPanel[],
      []
    );
  };

  // make sure this is properly memoized, or
  // the entire section will rerender on every update
  const usedPanelSettings = React.useMemo(() => {
    const usedXAxisSettings = _.pick(
      localPanelSettings.xAxisActive
        ? localPanelSettings
        : workspacePanelSettings,
      'xAxis',
      'xAxisMin',
      'xAxisMax',
      'xAxisActive'
    );

    const usedSmoothingSettings = _.pick(
      localPanelSettings.smoothingActive
        ? localPanelSettings
        : workspacePanelSettings,
      'smoothingWeight',
      'smoothingType',
      'smoothingActive'
    );

    return {
      ...usedXAxisSettings,
      ...usedSmoothingSettings,
      ignoreOutliers: workspacePanelSettings.ignoreOutliers,
      useRunsTableGroupingInPanels:
        workspacePanelSettings.useRunsTableGroupingInPanels,
    };
  }, [localPanelSettings, workspacePanelSettings]);

  const {setExportingSectionRefID} = useContext(PanelBankUpdaterContext);

  const sortPanels = useAllPanelBankSectionConfigsAction(
    [panelBankSectionConfigRef],
    PanelBankSectionConfigActions.sortPanels
  );

  const activePanelCount = activePanelRefs.length;
  const totalPanelCount = panelBankSectionConfig.panels.length;

  const isMobile = panelBankWidth <= 768; // tablet breakpoint from globals.less
  const isHiddenPanelsSection =
    panelBankSectionConfig.name === PANEL_BANK_HIDDEN_SECTION_NAME;

  const SectionComponent =
    // Don't use grid section (custom layout) on mobile
    !isMobile &&
    // Don't use grid section if searching (otherwise you get weird blank spaces in the layout)
    !isSearching &&
    panelBankSectionConfig.type === 'grid'
      ? PanelBankGridSection
      : PanelBankFlowSection;

  const MAX_CUSTOM_PANEL_COUNT = 50; // panel limit for custom layout (grid) sections

  const sectionMenuOptions = _.compact([
    !isMobile && {
      key: 'toggleSectionType',
      text: `Switch to ${
        panelBankSectionConfig.type === 'grid' ? 'standard' : 'custom'
      } layout`,
      icon: `wbic-ic-${
        panelBankSectionConfig.type === 'grid' ? 'standard' : 'custom'
      }-layout`,
      onClick: () => {
        // Disable custom layout if there's more than MAX_CUSTOM_PANEL_COUNT panels
        if (
          panelBankSectionConfig.type !== 'grid' &&
          activePanelCount > MAX_CUSTOM_PANEL_COUNT
        ) {
          toast(
            `Custom Layout is limited to ${MAX_CUSTOM_PANEL_COUNT} panels.`
          );
        } else {
          toggleType();
        }
      },
    },
    {
      key: 'sortPanelsInSection',
      text: 'Sort panels A-Z',
      icon: 'wbic-ic-sort',
      onClick: sortPanels,
    },
    {
      key: 'addSectionAbove',
      text: 'Add section above',
      icon: 'wbic-ic-add-above',
      onClick: addSectionAbove,
    },
    {
      key: 'addSectionBelow',
      text: 'Add section below',
      icon: 'wbic-ic-add-below',
      onClick: addSectionBelow,
    },
    {
      key: 'renameSection',
      text: 'Rename section',
      icon: 'wbic-ic-edit',
      onClick: () => setIsEditingName(true),
    },
    {
      key: 'addSectionToReport',
      text: 'Add section to report',
      icon: 'wbic-ic-report',
      onClick: () => setExportingSectionRefID(panelBankSectionConfigRef.id),
    },
    {
      key: 'deleteSection',
      text: 'Delete section',
      icon: 'wbic-ic-delete',
      onClick: deleteSection,
    },
  ]);
  const sectionName = panelBankSectionConfig.name;

  return (
    <>
      <DropTarget
        isValidDropTarget={({dragRef, dragData}) => {
          return (
            isPanelBankSection(dragRef) ||
            (isPanel(dragRef) &&
              !isDraggingWithinSection(panelBankSectionConfigRef, dragData))
          );
        }}
        partRef={panelBankSectionConfigRef}
        getClassName={({dragRef, dropRef}) => {
          const elementClassNames = ['panel-bank__section'];
          if (!sectionIsOpen) {
            elementClassNames.push('collapsed');
          }
          if (isHiddenPanelsSection) {
            elementClassNames.push('panel-bank__section__hidden-panels');
          }
          if (dragRef != null && isPanel(dragRef)) {
            // always added when dragging a panel, even if the panel is not in this section
            elementClassNames.push('dragging-panel');
          }
          if (
            dropRef != null &&
            !_.isEqual(dragRef, dropRef) &&
            _.isEqual(panelBankSectionConfigRef, dropRef)
          ) {
            if (isPanelBankSection(dragRef)) {
              elementClassNames.push('active-section-drop-target');
            }
            if (isPanel(dragRef) && (!sectionIsOpen || totalPanelCount === 0)) {
              elementClassNames.push('active-box-drop-target');
            }
          }
          return elementClassNames.join(' ');
        }}
        onDrop={({dragRef, dragData}) => {
          // Drag panel onto section
          if (isPanel(dragRef) && dragData) {
            movePanel(
              dragRef,
              dragData.fromSectionRef,
              panelBankSectionConfigRef,
              0
            );
          }
          // Drag section onto section
          if (isPanelBankSection(dragRef)) {
            if (!_.isEqual(dragRef, panelBankSectionConfigRef)) {
              moveSectionBefore(dragRef, panelBankSectionConfigRef);
            }
          }
        }}>
        <DragSource partRef={panelBankSectionConfigRef}>
          <div className="panel-bank__section-title">
            <span
              className="panel-bank__section-title-left"
              onClick={toggleSectionIsOpen}>
              <LegacyWBIcon
                className={sectionIsOpen ? 'open' : undefined}
                name="next"
              />
              {isSearching ? (
                // Searching - display highlighted title
                <>
                  {makeHighlightSearchTitle(sectionName)}
                  <Label className="count-label">
                    {isSectionNameSearchMatch
                      ? activePanelCount
                      : `${panelSearchMatchCount} of ${activePanelCount}`}
                  </Label>
                </>
              ) : (
                // Not searching - display editable title
                <>
                  <EditableLabel
                    title={sectionName}
                    onSave={updateName}
                    onBlur={() => setIsEditingName(false)}
                    editing={isEditingName}
                    serverText={sectionName}
                    onClick={() => {
                      // we use the menu option to trigger editing mode, rather than direct click
                      return;
                    }}
                  />
                  <Label className="count-label">{activePanelCount}</Label>
                </>
              )}
            </span>
            {!isHiddenPanelsSection && (
              <>
                <DragHandle
                  className="panel-bank__section__drag-handle"
                  partRef={panelBankSectionConfigRef}
                  key={`${panelBankSectionConfig.isOpen}${panelBankSectionConfig.name}${activePanelCount}`}>
                  <LegacyWBIcon
                    title=""
                    name="handle"
                    onClick={(e: React.SyntheticEvent) => e.stopPropagation()}
                  />
                </DragHandle>
                <div className="panel-bank__section-title-right">
                  <Popup
                    content="More"
                    position="bottom right"
                    size="small"
                    trigger={
                      <div
                        className={classNames({
                          'panel-section-options-visible': isSettingsPopupOpen,
                          'panel-section-options': !isSettingsPopupOpen,
                        })}>
                        <PopupDropdown
                          trigger={
                            <LegacyWBIcon
                              name="overflow"
                              size="big"
                              onClick={(e: React.SyntheticEvent) =>
                                e.stopPropagation()
                              }
                            />
                          }
                          options={sectionMenuOptions}
                          position="bottom left"
                          offset={'-320px, -16px'}
                        />
                      </div>
                    }
                  />
                  <LocalPanelSettingsComponentControlled
                    runSetRefs={runSetRefs}
                    useAllLinePlotPanels={useAllLinePlotPanels}
                    workspacePanelSettingsRef={workspacePanelSettingsRef}
                    localPanelSettingsRef={localPanelSettingsRef}
                    settingsPopupOpen={isSettingsPopupOpen}
                    setSettingsPopupOpen={setIsSettingsPopupOpen}
                  />
                  {addVisButton}
                </div>
              </>
            )}
          </div>

          {/* We're using this wrapper with {display: none} because a) we don't want to render the section if it's never opened,
           and b) we don't want to re-mount an open section if we close and re-open it. */}
          <div style={sectionIsOpen ? {} : {display: 'none'}}>
            {shouldRenderSection && (
              <SectionComponent
                panelBankSectionConfigRef={panelBankSectionConfigRef}
                panelBankWidth={panelBankWidth}
                activePanelRefs={activePanelRefs}
                inactivePanelRefs={inactivePanelRefs}
                panelSearchResults={panelSearchResults}
                panelSearchMatchCount={panelSearchMatchCount}
                isSectionNameSearchMatch={isSectionNameSearchMatch}
                movePanelBetweenSections={movePanel}
                addVisButton={addVisButton}
                // HAXX: this prop is not used, but needed to make the PanelBankFlowSection
                // rerender when settings are changed
                panelSettings={usedPanelSettings}
                renderPanel={panelRef => (
                  <PanelContainer
                    customRunColorsRef={customRunColorsRef}
                    defaultMoveToSectionRef={defaultMoveToSectionRef}
                    historyKeyInfo={historyKeyInfo}
                    isReadOnly={readOnly}
                    panelBankSectionConfigRef={panelBankSectionConfigRef}
                    panelBankConfigRef={panelBankConfigRef}
                    panelRef={panelRef}
                    panelSettings={usedPanelSettings}
                    runSetRefs={runSetRefs}
                  />
                )}
              />
            )}
          </div>
        </DragSource>
      </DropTarget>
      <div className="panel-bank__divider" />
    </>
  );
};
